import useCheckPC from "@repo/hooks/utils/useCheckPC";
import { transformDate } from "@repo/utils/src/number";
import { useEffect, useMemo, useRef, useState } from "react";

import type { IProductLaunch } from "@utils/product";

const DELAY_TIME = 1000;

const useDropDate = (launchData: IProductLaunch) => {
  const { isPC } = useCheckPC();

  // 드로우 상태 + 타이머  표기값 세팅
  const [dropTimer, setDropTimer] = useState("Loading...");

  // 드로우 상태, 타이머 남은 시간 정의
  const [dropStatus, setDropStatus] = useState("Loading");
  const [dropTimerRaw, setDropTimerRaw] = useState(0);
  const [timerValid, setTimerValid] = useState(true);

  // 드로우 타이머 객체 만들기
  const [dropTimerObject, setDropTimerObject] = useState({ day: 0, hour: 0, min: 0, sec: 0 });

  const savedCallback = useRef<() => void>(); // 최근에 들어온 callback을 저장할 ref를 하나 만든다.
  const today = new Date();
  const dropTime = useMemo(() => (launchData.time ? launchData.time : []), [launchData]);
  const dropStart = useMemo(() => (dropTime.length > 0 ? new Date(dropTime[0]) : new Date()), [dropTime]);
  const dropEnd = useMemo(() => (dropTime.length > 1 ? new Date(dropTime[1]) : new Date()), [dropTime]);

  useEffect(() => {
    setDropStatus("Loading");
  }, [dropTime]);

  const handleDropDate = () => {
    if (dropTimerObject.sec !== 0) {
      setDropTimer(
        `${dropStatus} - ${!isPC ? "\n" : ""}${dropTimerObject.day}d : ${String(dropTimerObject.hour).padStart(2, "0")}h :
         ${String(dropTimerObject.min).padStart(2, "0")}m : ${String(dropTimerObject.sec).padStart(2, "0")}s`,
      );
    }
  };

  const dropStartDate = useMemo(() => transformDate(dropStart), [dropStart]);
  const dropEndDate = useMemo(() => transformDate(dropEnd), [dropEnd]);

  // setInterval 대체 Custom Hook
  function useInterval(callback: () => void) {
    useEffect(() => {
      savedCallback.current = callback; // callback이 바뀔 때마다 ref를 업데이트 해준다.
    }, [callback]);

    // delay가 바뀔 때마다 새로 실행된다.
    useEffect(() => {
      // 만약 delay가 null이 아니라면
      const id = setInterval(() => {
        if (savedCallback.current) savedCallback.current(); // tick이 실행되면 callback 함수를 실행시킨다.
      }, DELAY_TIME); // delay에 맞추어 interval을 새로 실행시킨다.

      return () => {
        clearInterval(id);
      }; // unmount될 때 clearInterval을 해준다.
    }, []);
  }

  useEffect(() => {
    const isDraw = Boolean(launchData.content);

    // 드로우
    if (isDraw) {
      // 드로우 종료
      if (dropEnd < today) {
        setDropStatus("Draw Closed");
        setDropTimer(`${dropStatus} - ${!isPC ? "\n" : ""}${dropEndDate}`);
        return;
      }

      // 드로우 진행중
      if (dropStart < today) {
        setDropStatus("Draw Opened");
        setDropTimerRaw(Math.floor(Math.abs(dropEnd.getTime() - today.getTime()) / 1000));
        return;
      }

      // 드로우 시작전
      if (dropStart > today) {
        setDropStatus("Upcoming");
        if (launchData.isDetail) setDropTimerRaw(Math.floor(Math.abs(dropStart.getTime() - today.getTime()) / 1000));
        else setDropTimer(`${dropStatus} - ${!isPC ? "\n" : ""}${dropStartDate}`);
      }
    } else {
      // 신상품
      // 신상품 발매전
      if (dropStart > today) {
        setDropStatus("Upcoming");
        if (launchData.isDetail) {
          setDropTimerRaw(Math.floor(Math.abs(dropStart.getTime() - today.getTime()) / 1000));
        } else {
          setDropTimer(`${dropStatus} - ${!isPC ? "\n" : ""}${dropStartDate}`);
        }
        return;
      }

      // 신상품 발매후
      if (dropStart < today) {
        setDropStatus("Released");
        setDropTimer(`${dropStatus} - ${!isPC ? "\n" : ""}${dropStartDate}`);
      }
    }
  }, [dropStart, timerValid]);

  useInterval(() => {
    if (dropStatus === "Draw Opened" || (dropStatus === "Upcoming" && launchData.isDetail)) {
      setDropTimerRaw((prev) => prev - 1);
      setDropTimerObject({
        day: Math.floor(dropTimerRaw / (60 * 60 * 24)),
        hour: Math.floor((dropTimerRaw / (60 * 60)) % 24),
        min: Math.floor((dropTimerRaw / 60) % 60),
        sec: Math.floor(dropTimerRaw % 60),
      });
    }

    setTimerValid(dropTimerRaw > 0);

    handleDropDate();
  });

  const releaseFormat = useMemo(() => {
    if (dropStartDate) {
      const [month, date, year] = dropStartDate.split(".");
      return `${year}년 ${month}월 ${date}일`;
    }

    return "";
  }, [dropStartDate]);

  return {
    dropTimerRaw,
    dropTimer,
    dropStatus,
    dropStartDate,
    dropEndDate,
    releaseFormat,
  };
};

export default useDropDate;
